/** Base monthly API query quotas per plan (mirrors backend PlanAPIRequestBase). */
export const API_PRICING_BASE_QUERIES: Record<string, number> = {
	Free: 250,
	Personal: 5_000,
	Team: 10_000,
	Enterprise: 25_000,
	// Legacy aliases (EmailGuard tier names)
	Launch: 5_000,
	Amplify: 10_000,
	Fleet: 25_000,
};

export type ApiQueryAddonTier = {
	queries: number;
	label: string;
	monthly_cents: number;
	yearly_cents: number;
	monthly_price_id?: string;
	yearly_price_id?: string;
};

export function formatQueryCount(value: number): string {
	if (value >= 1_000_000) {
		return `${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}M`;
	}
	if (value >= 1_000) {
		return `${(value / 1_000).toFixed(value % 1_000 === 0 ? 0 : 1)}K`;
	}
	return value.toLocaleString();
}

export function effectiveApiQueries(
	planName: string,
	addonQueries: number,
): number {
	const base = API_PRICING_BASE_QUERIES[planName] ?? 0;
	return base + addonQueries;
}

export function selectAddonPriceId(
	tier: ApiQueryAddonTier,
	isAnnual: boolean,
): string | undefined {
	return isAnnual ? tier.yearly_price_id : tier.monthly_price_id;
}

/** Slider index 0 = no add-on; 1..n maps to sorted tiers. */
export function addonQueriesForSliderIndex(
	index: number,
	tiers: ApiQueryAddonTier[],
): number {
	if (index <= 0) return 0;
	const sorted = [...tiers].sort((a, b) => a.queries - b.queries);
	return sorted[index - 1]?.queries ?? 0;
}

export function addonTierForSliderIndex(
	index: number,
	tiers: ApiQueryAddonTier[],
): ApiQueryAddonTier | undefined {
	if (index <= 0) return undefined;
	const sorted = [...tiers].sort((a, b) => a.queries - b.queries);
	return sorted[index - 1];
}

export function sliderIndexForAddonQueries(
	addonQueries: number,
	tiers: ApiQueryAddonTier[],
): number {
	if (addonQueries <= 0) return 0;
	const sorted = [...tiers].sort((a, b) => a.queries - b.queries);
	const idx = sorted.findIndex((t) => t.queries === addonQueries);
	return idx >= 0 ? idx + 1 : 0;
}

/** Static add-on tiers when the billing API is unavailable (preview / fallback). */
export const QUERY_ADDON_TIERS_FALLBACK: ApiQueryAddonTier[] = [
	{
		queries: 5_000,
		label: "+5K queries/mo",
		monthly_cents: 1000,
		yearly_cents: 10000,
	},
	{
		queries: 10_000,
		label: "+10K queries/mo",
		monthly_cents: 1800,
		yearly_cents: 18000,
	},
	{
		queries: 25_000,
		label: "+25K queries/mo",
		monthly_cents: 4000,
		yearly_cents: 40000,
	},
	{
		queries: 50_000,
		label: "+50K queries/mo",
		monthly_cents: 7500,
		yearly_cents: 75000,
	},
	{
		queries: 100_000,
		label: "+100K queries/mo",
		monthly_cents: 14000,
		yearly_cents: 140000,
	},
	{
		queries: 250_000,
		label: "+250K queries/mo",
		monthly_cents: 30000,
		yearly_cents: 300000,
	},
	{
		queries: 500_000,
		label: "+500K queries/mo",
		monthly_cents: 55000,
		yearly_cents: 550000,
	},
	{
		queries: 750_000,
		label: "+750K queries/mo",
		monthly_cents: 75000,
		yearly_cents: 750000,
	},
	{
		queries: 975_000,
		label: "+975K queries/mo",
		monthly_cents: 90000,
		yearly_cents: 900000,
	},
];

/** @deprecated Use QUERY_ADDON_TIERS_FALLBACK */
export const MARKETING_QUERY_ADDON_TIERS = QUERY_ADDON_TIERS_FALLBACK;
